/**
 * useApplyFlow Hook
 * Encapsulates all state management for the apply flow
 * Created: January 23, 2026
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useTenant } from '@/lib/tenant-context';
import { useAuth } from '@/lib/auth-context';
import { jobsApi, applyApi, candidateApi, getAuthToken } from '@/lib/api';
import { probeForSubmittedApplication, isIndeterminateSubmitError, isAlreadyAppliedError } from '@/lib/apply-recovery';
import { recordJobAlertApplied } from '@/lib/job-alert-attribution';
import { API_BASE } from '@/lib/config';
import { DEFAULT_DIAL_CODE, toE164, parsePhone, isValidNationalNumber } from '@/lib/phone';
import type { FlowStep, JobInfo, Question, ParsedData, MatchAnalysis } from '../types';
import { PLATFORM_QUESTIONS } from '../types';
import { resolveReusableResumeId } from './resume-reuse';
import { buildArenaAnswersPayload } from './arena-answers';

// Version of the terms document the REVIEW-step checkbox refers to. Bump when
// the candidate-facing terms change — persisted per application
// (job_applications.terms_version) as the ToS acceptance audit trail.
const TERMS_VERSION = '2026-06';

/* eslint-disable no-console */
// Verbose apply-flow traces are dev-only; NODE_ENV is build-inlined so these
// console.logs are dead-code-eliminated from the production bundle. Errors are
// kept in prod. Audit ref: §4 (debug logging leaked into the live apply flow).
const DEBUG = process.env.NODE_ENV !== 'production';
const log = (service: string, action: string, data?: unknown) => {
  if (DEBUG) console.log(`[${new Date().toISOString().split('T')[1].split('.')[0]}] 🔵 ${service} → ${action}`, data || '');
};
const logSuccess = (service: string, action: string, data?: unknown) => {
  if (DEBUG) console.log(`[${new Date().toISOString().split('T')[1].split('.')[0]}] ✅ ${service} → ${action}`, data || '');
};
const logError = (service: string, action: string, error?: unknown) => {
  console.error(`[${new Date().toISOString().split('T')[1].split('.')[0]}] ❌ ${service} → ${action}`, error || '');
};
/* eslint-enable no-console */

// ----------------------------------------------------------------------------
// Job-share attribution helpers (P1 — May 2026)
// ----------------------------------------------------------------------------
// Recruiter attribution is first-touch: if the candidate arrived via a
// vhire.com/j/{code} share link, the landing handler set a 30-day signed
// cookie `vhire_ref`. We also accept ?ref= in the URL (in case the cookie
// was blocked or the candidate is on a fresh device). URL wins over cookie
// since it represents the most recent intent.
//
// The captured share code is sent to the apply API, which writes
// source_share_code + source_recruiter_id on the application row and
// increments the share-link's application_count.
// ----------------------------------------------------------------------------

const REF_COOKIE_NAME = 'vhire_ref';

function readShareCodeFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${REF_COOKIE_NAME}=([^;]+)`));
  if (!match) return null;
  // The cookie value is base64-encoded JSON { code, jobId, setAt } from the
  // /j/{code} handler — for now we just extract the share code via a simple
  // parse. If the format is opaque/signed we still pass through, and the
  // backend (or future signature verification) decides validity.
  try {
    const decoded = atob(decodeURIComponent(match[1]));
    const parsed = JSON.parse(decoded) as { code?: string };
    return parsed.code ?? null;
  } catch {
    // Fallback: maybe it's a plain share code (not yet signed)
    return decodeURIComponent(match[1]);
  }
}

function readShareCodeFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('ref');
}

/** Returns the active share code for this apply session (URL param > cookie > null). */
function captureShareCode(): string | null {
  return readShareCodeFromUrl() ?? readShareCodeFromCookie();
}

// Session persistence helpers (Feb 24, 2026 - survive tab close)
const SESSION_KEY = (jId: string) => `apply_session_${jId}`;
// Backend KV TTL bumped to 7 days on 2026-05-04 — keep the localStorage TTL
// slightly shorter so we evict stale local copies before the backend does.
const SESSION_TTL_MS = 6 * 24 * 60 * 60 * 1000; // 6 days

interface PersistedSession {
  sessionId: string;
  step: FlowStep;
  email: string;
  fullName: string;
  // ✅ "phone" is the canonical contact-number field across the apply flow API
  // and DB. The UI label shows "Mobile Number" for India familiarity, and the
  // resume parser persists into a column called `mobile` (kept as-is to avoid
  // a risky migration), but everywhere else this is `phone`.
  phone: string;
  // E.164 dial code paired with `phone` (the national number). Optional for
  // back-compat with drafts written before international support; missing →
  // India default on load.
  dialCode?: string;
  matchScore: number | null;
  matchAnalysis: MatchAnalysis | null;
  parsedData: ParsedData | null;
  arenaSessionId: string | null;
  tenantQuestions: Question[];
  jobQuestions: Question[];
  aiQuestions: Question[];
  platformQuestions?: Question[];
  createdAt: number;
}

interface ProfileCandidatePrefill {
  fullName?: string | null;
  first_name?: string | null;
  firstName?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  mobile?: string | null;
  phone?: string | null;
  phone_number?: string | null;
  current_ctc?: string | number | null;
  currentCtc?: string | number | null;
  expected_ctc?: string | number | null;
  expectedCtc?: string | number | null;
  notice_period_days?: number | null;
  noticePeriodDays?: number | null;
  serving_notice?: boolean | null;
  servingNotice?: boolean | null;
  last_working_day?: string | number | Date | null;
  lastWorkingDay?: string | number | Date | null;
  current_location?: string | null;
  currentLocation?: string | null;
  preferred_locations?: string | string[] | null;
  preferredLocations?: string | string[] | null;
  work_mode?: string | null;
  workMode?: string | null;
  willing_to_relocate?: boolean | null;
  willingToRelocate?: boolean | null;
  open_to_work?: boolean | null;
  openToWork?: boolean | null;
  date_of_birth?: string | number | Date | null;
  dateOfBirth?: string | number | Date | null;
  nationality?: string | null;
  marital_status?: string | null;
  maritalStatus?: string | null;
  employment_types?: string | string[] | null;
  employmentTypes?: string | string[] | null;
  gender?: string | null;
}

function loadPersistedSession(jobId: string): PersistedSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY(jobId));
    if (!raw) return null;
    const session: PersistedSession = JSON.parse(raw);
    // Expire stale sessions
    if (Date.now() - session.createdAt > SESSION_TTL_MS) {
      localStorage.removeItem(SESSION_KEY(jobId));
      return null;
    }
    return session;
  } catch { return null; }
}

function savePersistedSession(jobId: string, session: PersistedSession) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(SESSION_KEY(jobId), JSON.stringify(session));
  } catch { /* storage full, ignore */ }
}

function clearPersistedSession(jobId: string) {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_KEY(jobId));
}

export function useApplyFlow(jobId: string) {
  const router = useRouter();
  const { tenant, isTenantDomain, isLoading: tenantLoading } = useTenant();
  const { user, isAuthenticated, isLoading: authLoading, verifyToken, login } = useAuth();

  // Restore persisted session on mount (Feb 24, 2026)
  const persisted = loadPersistedSession(jobId);

  // Flow state
  const [step, setStep] = useState<FlowStep>(persisted ? persisted.step : 'auth');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isBackgroundProcessing, setIsBackgroundProcessing] = useState(false);

  // Capture state
  const [email, setEmail] = useState(persisted?.email || '');
  const [fullName, setFullName] = useState(persisted?.fullName || '');
  // ✅ Apply-flow state uses `phone` (canonical name across API + DB).
  // Read from either persisted.phone OR legacy persisted.mobile for backwards
  // compat with localStorage written before the rename (May 4, 2026).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const legacyMobile = (persisted as any)?.mobile as string | undefined;
  const [phone, setPhone] = useState<string>(persisted?.phone || legacyMobile || '');
  // Country dial code paired with `phone`. Defaults to India; restored from the
  // draft, then overwritten by the candidate's saved profile during prefill.
  const [dialCode, setDialCode] = useState<string>(persisted?.dialCode || DEFAULT_DIAL_CODE);

  // Job state
  const [job, setJob] = useState<JobInfo | null>(null);

  // Resume state
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedData | null>(persisted?.parsedData || null);

  // Returning candidate state (Feb 11, 2026 - Issue 1 fix)
  const [isReturningCandidate, setIsReturningCandidate] = useState(false);
  const [hasExistingResume, setHasExistingResume] = useState(false);
  const [existingResumeId, setExistingResumeId] = useState<string | null>(null);
  // Resume age + freshness for the returning-candidate "view / update" nudge
  // (2026-06-23). Sourced from getOnboardingStatus steps.resume; consumed
  // defensively — `resumeFresh` stays undefined when the backend hasn't shipped
  // the field yet (deploy order is backend→frontend) so callers can degrade.
  const [existingResumeCreatedAt, setExistingResumeCreatedAt] = useState<string | null>(null);
  const [existingResumeFresh, setExistingResumeFresh] = useState<boolean | undefined>(undefined);
  const [platformAnswersFresh, setPlatformAnswersFresh] = useState(false);
  const reusableExistingResumeId = resolveReusableResumeId(hasExistingResume, existingResumeId);
  const canReuseExistingResume = !!reusableExistingResumeId;
  // Already-applied-to-this-job (issue C, June 2026). When true the UI offers
  // "view your application" instead of silently re-running the apply flow; the
  // candidate can still choose to update their answers (re-submit = update).
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [existingApplicationId, setExistingApplicationId] = useState<string | null>(null);

  // Auto-skip capture state (May 6, 2026)
  // True when the candidate is fully recognized (profile + resume) and we silently
  // bypass the capture step. Drives step numbering + the resume-replace banner UI.
  const [autoSkippedCapture, setAutoSkippedCapture] = useState(false);

  // Session & Match state
  const [sessionId, setSessionId] = useState<string | null>(persisted?.sessionId || null);
  const [matchScore, setMatchScore] = useState<number | null>(persisted?.matchScore ?? null);
  const [matchAnalysis, setMatchAnalysis] = useState<MatchAnalysis | null>(persisted?.matchAnalysis || null);

  // Arena session for AI MCQ tracking
  const [arenaSessionId, setArenaSessionId] = useState<string | null>(persisted?.arenaSessionId || null);
  const [badgesEarned, setBadgesEarned] = useState<Array<{ name: string; iconEmoji: string; level?: string }>>([]);

  // Questions state.
  // platformQuestions: source of truth is the backend D1 platform_questions table.
  // We seed with the static PLATFORM_QUESTIONS const for instant first-paint, then
  // replace once the apply/start (or context) response arrives so D1 additions
  // automatically flow into the UI without a frontend change.
  const [tenantQuestions, setTenantQuestions] = useState<Question[]>(persisted?.tenantQuestions || []);
  const [jobQuestions, setJobQuestions] = useState<Question[]>(persisted?.jobQuestions || []);
  const [aiQuestions, setAiQuestions] = useState<Question[]>(persisted?.aiQuestions || []);
  const [platformQuestions, setPlatformQuestions] = useState<Question[]>(persisted?.platformQuestions || PLATFORM_QUESTIONS);

  // Log session restore (Feb 24, 2026)
  useEffect(() => {
    if (persisted) {
      log('SESSION_RESTORE', `Restored session from localStorage`, {
        sessionId: persisted.sessionId,
        step: persisted.step,
        age: `${Math.round((Date.now() - persisted.createdAt) / 60000)}min`,
        hasMatch: persisted.matchScore !== null,
        questionCounts: {
          tenant: persisted.tenantQuestions.length,
          job: persisted.jobQuestions.length,
          ai: persisted.aiQuestions.length
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist step changes to localStorage (Feb 24, 2026)
  // Only update if we have an active session (past capture step)
  useEffect(() => {
    if (!sessionId) return;
    const persistableSteps: FlowStep[] = ['questions', 'job_questions', 'ai_questions', 'review'];
    if (persistableSteps.includes(step)) {
      const existing = loadPersistedSession(jobId);
      if (existing && existing.step !== step) {
        savePersistedSession(jobId, { ...existing, step });
      }
    }
  }, [step, sessionId, jobId]);
  
  // Initialize answers from localStorage if available (Feb 11, 2026 - preserve midway drop-off)
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`apply_answers_${jobId}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          log('RESTORE', `Restored ${Object.keys(parsed).length} saved answers from localStorage`);
          return parsed;
        } catch { /* ignore */ }
      }
    }
    return {};
  });
  
  // Persist answers to localStorage on change (Feb 11, 2026)
  useEffect(() => {
    if (typeof window !== 'undefined' && Object.keys(answers).length > 1) {
      localStorage.setItem(`apply_answers_${jobId}`, JSON.stringify(answers));
    }
  }, [answers, jobId]);

  // AI Question timer state
  const [currentAiQuestionIndex, setCurrentAiQuestionIndex] = useState(0);
  const [aiQuestionTimeLeft, setAiQuestionTimeLeft] = useState(10);
  // Interval id for the per-question AI countdown. Cleared synchronously when the
  // candidate answers so an in-flight tick can't double-advance the question index
  // or double-complete the arena session (FE-2).
  const aiTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const arenaCompletedRef = useRef(false);
  // Per-question elapsed time (AI quiz) for the application_answers projection
  // and the arena completion payload.
  const answerTimingsRef = useRef<Record<string, { timeTakenMs?: number }>>({});
  // Synchronous mirror of `answers`: the quiz can end inside a timer tick or a
  // setTimeout, before React has committed the last answer to state.
  const answersRef = useRef(answers);
  useEffect(() => { answersRef.current = answers; }, [answers]);

  // Terms
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Collapsible sections state for review
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Complete Arena session and get badges
  const completeArenaSession = useCallback(async () => {
    if (!arenaSessionId || !user?.candidateId || !job?.id) return;
    // Idempotent: the timer and the answer-advance can both reach the end of the
    // quiz; only the first caller should hit arena/complete (FE-2).
    if (arenaCompletedRef.current) return;
    arenaCompletedRef.current = true;

    try {
      const token = getAuthToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Only the picked option per question is sent; the server scores it
      // against the answer key it stored for this session. Unanswered
      // (timed-out) questions go as null.
      const answersPayload = buildArenaAnswersPayload(aiQuestions, answersRef.current, answerTimingsRef.current);

      const response = await fetch(`${API_BASE}/arena/complete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sessionId: arenaSessionId,
          candidateId: user.candidateId,
          jobId: job.id,
          answers: answersPayload,
        })
      });

      if (!response.ok) {
        throw new Error(`arena/complete returned ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.badgesEarned?.length > 0) {
        setBadgesEarned(result.badgesEarned);
        log('ARENA', `Badges earned: ${result.badgesEarned.length}`);
      }
    } catch (error) {
      // Allow a later end-of-quiz path (e.g. re-entering the quiz from review)
      // to retry; the server side is idempotent.
      arenaCompletedRef.current = false;
      logError('ARENA', 'Failed to complete session', error);
    }
  }, [arenaSessionId, user?.candidateId, job?.id, aiQuestions]);

  // AI Question Timer Effect
  useEffect(() => {
    if (step !== 'ai_questions' || aiQuestions.length === 0 || aiQuestionTimeLeft <= 0) return;

    const timer = setInterval(() => {
      setAiQuestionTimeLeft(prev => {
        if (prev <= 1) {
          if (currentAiQuestionIndex < aiQuestions.length - 1) {
            setCurrentAiQuestionIndex(i => i + 1);
            return aiQuestions[currentAiQuestionIndex + 1]?.timeLimit || 10;
          } else {
            completeArenaSession();
            setStep('review');
            return 0;
          }
        }
        return prev - 1;
      });
    }, 1000);
    aiTimerRef.current = timer;

    return () => {
      clearInterval(timer);
      if (aiTimerRef.current === timer) aiTimerRef.current = null;
    };
  }, [step, currentAiQuestionIndex, aiQuestions, aiQuestionTimeLeft, completeArenaSession]);

  // Check for magic link token in URL.
  // ✅ Guarded against StrictMode double-mount, navigation re-runs, and the
  // already-consumed-token race (May 4, 2026). The same token must only be
  // verified once per page load even if React mounts the effect twice.
  const magicLinkVerifiedRef = useRef<string | null>(null);
  // ✅ BFF response cache (May 5, 2026) — populated by applyApi.getContext on
  // mount; the session-resume branch reads from here instead of firing a
  // separate /apply/session call.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const applyContextRef = useRef<any>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) return;
    if (magicLinkVerifiedRef.current === token) return; // already attempted this token
    magicLinkVerifiedRef.current = token;

    log('AUTH', 'Verifying magic link token');
    verifyToken(token).then(result => {
      if (result.success) {
        logSuccess('AUTH', 'Magic link verified');
        // Only strip the token AFTER the auth context has written user/token
        // so a refresh during the verify can't lose state.
        window.history.replaceState({}, '', `/apply/${jobId}`);
      } else {
        // "Already used" is benign if we're already authenticated — the user
        // probably hit refresh after a successful verify. Fall through silently.
        const isAlreadyUsed = result.error && /already.*used|consumed|invalid/i.test(result.error);
        if (isAlreadyUsed && isAuthenticated) {
          log('AUTH', 'Magic link already consumed but session is valid — clearing token');
          window.history.replaceState({}, '', `/apply/${jobId}`);
          return;
        }
        logError('AUTH', 'Magic link verification failed', result.error);
        setError('Login link expired or invalid. Please try again.');
      }
    });
  }, [jobId, verifyToken, isAuthenticated]);

  // Auto-skip auth step and populate user data when authenticated
  // Also check onboarding status for returning candidates (Feb 11, 2026 - Issue 1 fix)
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      logSuccess('AUTH', 'User authenticated', { candidateId: user?.candidateId });
      if (user?.fullName) setFullName(user.fullName);
      if (user?.email) setEmail(user.email);
      
      // ✅ Single BFF call — bundles onboarding + full profile + session + job
      // (May 5, 2026). Replaces the previous two-step chain that fired
      // getOnboardingStatus then getFullProfile sequentially. The session and
      // job slices come along for free; the existing session-resume branch
      // below picks them up via applyContextRef when available.
      applyApi.getContext(jobId).then(async ctx => {
        const ctxData = ctx?.data;
        if (ctx?.success && ctxData) {
          // Stash the session + job slices for the session-resume branch.
          applyContextRef.current = ctxData;
        }
        // Onboarding result reshaped into the legacy shape so the rest of the
        // body can stay unchanged.
        const result = {
          success: !!ctxData?.onboarding,
          data: ctxData?.onboarding || undefined,
        };
        if (result.success && result.data) {
          const { steps } = result.data;

          if (steps.resume.completed && steps.resume.resumeId) {
            setHasExistingResume(true);
            setExistingResumeId(steps.resume.resumeId);
            setExistingResumeCreatedAt(steps.resume.createdAt ?? null);
            setExistingResumeFresh(steps.resume.resumeFresh);
            setIsReturningCandidate(true);
            log('RETURNING', `Has existing resume: ${steps.resume.resumeId}`);
          }

          // ✅ May 6, 2026: Use the 6-month freshness window from the backend.
          // Previously we marked answers as "fresh" whenever they were ever
          // completed; that meant a candidate who answered 2 years ago would
          // skip the questions step. Now `fresh` is true only if the answers
          // were updated in the last 6 months.
          if (steps.platformQuestions.fresh) {
            setPlatformAnswersFresh(true);
            log('RETURNING', `Platform answers fresh (lastAnsweredAt=${steps.platformQuestions.lastAnsweredAt})`);
          }

          // Profile prefill — use the BFF's bundled profile slice (no second hop).
          try {
            const profileResult = {
              success: !!ctxData?.profile,
              data: ctxData?.profile || undefined,
            };
            if (profileResult.success && profileResult.data?.candidate) {
              const c = profileResult.data.candidate as ProfileCandidatePrefill;
              const prefillAnswers: Record<string, string> = {};

              // ✅ Pre-fill basic Your Info fields from saved profile (May 4, 2026)
              // Auth context only has fullName from the magic-link token; phone is
              // never in the auth user object. Pull from candidate record so a
              // returning candidate doesn't have to retype name + mobile every apply.
              const profileFullName = c.fullName || [c.first_name || c.firstName, c.last_name || c.lastName].filter(Boolean).join(' ').trim();
              setFullName(prev => (prev && prev.trim()) ? prev : (profileFullName || prev));
              const profilePhone = c.mobile || c.phone || c.phone_number || '';
              if (profilePhone) {
                // Split the stored value (E.164 or legacy national) back into
                // dial code + national number so the country is preserved.
                const parsed = parsePhone(String(profilePhone));
                setPhone(prev => (prev && prev.trim()) ? prev : (parsed.national || prev));
                // Only adopt the profile's country if the user hasn't typed yet.
                if (parsed.national && !(phone && phone.trim())) setDialCode(parsed.dialCode);
                if (parsed.national) prefillAnswers.platform_mobile = toE164(parsed.dialCode, parsed.national);
              }

              // Map profile fields to platform question IDs
              if (c.current_ctc || c.currentCtc) prefillAnswers.platform_current_ctc = String(c.current_ctc || c.currentCtc);
              if (c.expected_ctc || c.expectedCtc) prefillAnswers.platform_expected_ctc = String(c.expected_ctc || c.expectedCtc);
              const days = c.notice_period_days ?? c.noticePeriodDays;
              if (days != null) {
                if (days === 0) prefillAnswers.platform_notice_period_days = 'Immediate';
                else if (days <= 15) prefillAnswers.platform_notice_period_days = '15 Days';
                else if (days <= 30) prefillAnswers.platform_notice_period_days = '30 Days';
                else if (days <= 60) prefillAnswers.platform_notice_period_days = '60 Days';
                else if (days <= 90) prefillAnswers.platform_notice_period_days = '90 Days';
                else prefillAnswers.platform_notice_period_days = '90+ Days';
              }
              const servingNotice = c.serving_notice ?? c.servingNotice;
              if (servingNotice != null) {
                prefillAnswers.platform_notice_period = servingNotice ? 'yes' : 'no';
              }
              const lwd = c.last_working_day ?? c.lastWorkingDay;
              if (lwd) {
                // LWD is stored as timestamptz — always normalize to YYYY-MM-DD
                const lwdDate = new Date(lwd);
                if (!isNaN(lwdDate.getTime())) {
                  const y = lwdDate.getUTCFullYear();
                  const m = String(lwdDate.getUTCMonth() + 1).padStart(2, '0');
                  const d = String(lwdDate.getUTCDate()).padStart(2, '0');
                  prefillAnswers.platform_last_working_day = `${y}-${m}-${d}`;
                }
              }
              const currentLocation = c.current_location ?? c.currentLocation;
              if (currentLocation) {
                prefillAnswers.platform_current_location = currentLocation;
              }
              const locs = c.preferred_locations ?? c.preferredLocations;
              if (locs) {
                prefillAnswers.platform_preferred_locations = Array.isArray(locs) ? JSON.stringify(locs) : locs;
              }
              const mode = c.work_mode ?? c.workMode;
              if (mode) {
                const modeMap: Record<string, string> = {
                  'office': 'Full-time Office',
                  'hybrid': 'Hybrid (Office + Remote)',
                  'remote': 'Remote Only',
                  'any': 'Any (No preference)'
                };
                prefillAnswers.platform_work_mode = modeMap[mode] || mode;
              }
              const willingToRelocate = c.willing_to_relocate ?? c.willingToRelocate;
              if (willingToRelocate != null) {
                prefillAnswers.platform_willing_to_relocate = willingToRelocate ? 'yes' : 'no';
              }
              const openToWork = c.open_to_work ?? c.openToWork;
              if (openToWork != null) {
                prefillAnswers.platform_open_to_work = openToWork ? 'Actively looking' : 'Open to opportunities';
              }
              const dob = c.date_of_birth ?? c.dateOfBirth;
              if (dob) {
                prefillAnswers.platform_dob = typeof dob === 'string' ? dob : new Date(dob).toISOString().split('T')[0];
              }
              if (c.nationality) prefillAnswers.platform_nationality = c.nationality;
              const maritalStatus = c.marital_status ?? c.maritalStatus;
              if (maritalStatus) prefillAnswers.platform_marital_status = maritalStatus;
              const et = c.employment_types ?? c.employmentTypes;
              if (et) {
                prefillAnswers.platform_employment_types = Array.isArray(et) ? JSON.stringify(et) : et;
              }
              if (c.gender) prefillAnswers.platform_gender = c.gender;
              
              if (Object.keys(prefillAnswers).length > 0) {
                setAnswers(prev => ({ ...prefillAnswers, ...prev }));
                log('PREFILL', `Pre-filled ${Object.keys(prefillAnswers).length} answers from profile`);
              }
            }
          } catch (err) {
            log('PREFILL', 'Failed to fetch profile for pre-fill', err);
          }
        }
      }).catch(err => {
        log('RETURNING', 'Failed to check onboarding status', err);
      });
      
      // Session restore: if no localStorage session, try backend (Feb 24, 2026 - Phase B)
      // Covers: cross-device, private browsing, cache cleared
      // ✅ May 5, 2026: prefer the BFF's bundled session slice; fall back to
      // a direct /apply/session call only if the BFF didn't return it.
      if (step === 'auth' && !sessionId) {
        const userEmail = user?.email || email;
        if (userEmail) {
          const bundledSession = applyContextRef.current?.session;
          const sessionPromise = bundledSession
            ? Promise.resolve(bundledSession)
            : applyApi.getSession(jobId, userEmail);
          sessionPromise.then(sessionResult => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sData = (sessionResult.data || sessionResult) as any;
            if (sessionResult.success && sData.sessionId) {
              log('SESSION_RESTORE', `Restored session from backend for ${userEmail}`, {
                sessionId: sData.sessionId,
                matchScore: sData.matchScore,
                questionsCount: sData.questions?.length
              });

              setSessionId(sData.sessionId);
              if (sData.matchScore !== undefined) setMatchScore(Math.min(100, sData.matchScore ?? 0));
              if (sData.matchAnalysis) setMatchAnalysis(sData.matchAnalysis);
              if (sData.arenaSessionId) setArenaSessionId(sData.arenaSessionId);
              // FE-4: surface already-applied on a restored session too, not just on a
              // fresh start. sData is `any`; these reads are no-ops until the backend
              // includes the fields in the session/context payload.
              // TODO(backend): getExistingSession should return alreadyApplied
              if (sData.alreadyApplied) {
                setAlreadyApplied(true);
                if (sData.existingApplicationId) setExistingApplicationId(String(sData.existingApplicationId));
              }

              if (sData.questions?.length > 0) {
                const platformFromApi = sData.questions.filter((q: { source?: string }) => q.source === 'platform');
                if (platformFromApi.length > 0) setPlatformQuestions(platformFromApi);
                setTenantQuestions(sData.questions.filter((q: { source?: string }) => q.source === 'tenant'));
                setJobQuestions(sData.questions.filter((q: { source?: string }) => q.source === 'job'));
                setAiQuestions(sData.questions.filter((q: { source?: string }) => q.source === 'ai'));
              }

              // Persist to localStorage for subsequent returns
              savePersistedSession(jobId, {
                sessionId: sData.sessionId,
                step: 'questions',
                email: userEmail,
                fullName: fullName || user?.fullName || '',
                phone,
                dialCode,
                matchScore: sData.matchScore ?? null,
                matchAnalysis: sData.matchAnalysis || null,
                parsedData: null,
                arenaSessionId: sData.arenaSessionId || null,
                tenantQuestions: sData.questions?.filter((q: { source?: string }) => q.source === 'tenant') || [],
                jobQuestions: sData.questions?.filter((q: { source?: string }) => q.source === 'job') || [],
                aiQuestions: sData.questions?.filter((q: { source?: string }) => q.source === 'ai') || [],
                platformQuestions: sData.questions?.filter((q: { source?: string }) => q.source === 'platform') || [],
                createdAt: Date.now()
              });

              setStep('questions');
              return; // Skip capture — session restored
            }
            // No backend session — normal flow to capture
            setStep('capture');
          }).catch(() => {
            setStep('capture');
          });
        } else {
          setStep('capture');
        }
      } else if (step === 'auth') {
        // Has localStorage session already — persisted step was restored in useState init
        // Just make sure we're not stuck on auth
        setStep('capture');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user, step]);

  // Fetch job details on mount
  useEffect(() => {
    const loadJob = async () => {
      if (!job) {
        log('JOB_SERVICE', `Fetching job ${jobId}`);
        try {
          const response = await jobsApi.get(jobId);
          if (response.success && response.data) {
            const jobData = response.data;
            setJob({
              id: jobId,
              title: jobData.title || 'Position',
              company: jobData.company || tenant?.company_name || 'Company',
              location: jobData.location || jobData.city || 'Remote',
              salary: jobData.salaryRange,
              experience: jobData.experienceLevel ?? undefined,
              skills: jobData.skills || [],
            });
            logSuccess('JOB_SERVICE', 'Job fetched', { title: jobData.title });
          } else {
            logError('JOB_SERVICE', 'Failed to fetch job', response.error);
          }
        } catch (err) {
          logError('JOB_SERVICE', 'Network error', err);
        }
      }
    };
    loadJob();
  }, [job, jobId, tenant?.company_name]);

  // Validate capture form
  const validateCaptureForm = useCallback((): boolean => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email');
      return false;
    }
    if (!fullName.trim() || fullName.trim().length < 2) {
      setError('Please enter your full name');
      return false;
    }
    if (!isValidNationalNumber(dialCode, phone)) {
      setError(dialCode === DEFAULT_DIAL_CODE
        ? 'Please enter a valid 10-digit mobile number'
        : 'Please enter a valid mobile number');
      return false;
    }
    // Resume is optional only when we have the actual resume id to send to /apply/start.
    if (!resumeFile && !canReuseExistingResume) {
      setError('Please upload your resume');
      return false;
    }
    return true;
  }, [email, fullName, phone, dialCode, resumeFile, canReuseExistingResume]);

  // Synchronous re-entry guard for application start (FE-3). Mirrors submittingRef:
  // set before the first await so a fast double-click or the auto-skip effect can't
  // fire two apply/start requests; released on every failure path so a retry works.
  const startingRef = useRef(false);
  // Audit 2026-07-09 HIGH-3: one-shot guard so a FAILED auto-skip does not re-arm
  // the auto-skip effect below. The failure paths reset autoSkippedCapture=false
  // (to return the user to the capture form), which without this would re-satisfy
  // the effect's condition and re-fire handleStartApplication → an infinite
  // /apply/start retry loop for any returning candidate on a transient error.
  // Gating on `error` would NOT work: setError(null) runs at the top of each
  // attempt. Manual retry (form submit → exported handleStartApplication) is
  // unaffected — this ref only gates the automatic effect.
  const autoSkipAttemptedRef = useRef(false);
  // Start application with resume
  const handleStartApplication = useCallback(async () => {
    if (startingRef.current) {
      log('APPLICATION_SERVICE', 'Start ignored — already in flight');
      return;
    }
    if (!validateCaptureForm()) {
      return;
    }
    
    // Resume is required unless returning candidate has a concrete reusable resume id.
    if (!resumeFile && !canReuseExistingResume) {
      setError('Please upload your resume');
      return;
    }

    startingRef.current = true;
    setIsLoading(true);
    setError(null);

    // Main-domain public job payloads intentionally omit the internal tenant
    // ownership id. The backend derives and validates tenant attribution from
    // the trusted job record; a tenant-subdomain context may still be supplied
    // as an optional consistency hint.
    const tenantId = tenant?.id || undefined;

    // Prepare file to send (may be null for returning candidates)
    let fileToSend: File | null = null;
    if (resumeFile) {
      try {
        const arrayBuffer = await resumeFile.arrayBuffer();
        fileToSend = new File([arrayBuffer], resumeFile.name, { type: resumeFile.type });
      } catch (err) {
        setError('Failed to read resume file. Please try uploading again.');
        setIsLoading(false);
        setAutoSkippedCapture(false);
        startingRef.current = false;
        return;
      }
    }

    setStep('questions');
    setIsBackgroundProcessing(true);
    setIsLoading(false);

    const existingResumeIdForStart = !fileToSend ? reusableExistingResumeId : undefined;

    log('APPLICATION_SERVICE', 'Starting enhanced apply (async)', {
      jobId, tenantId, email, fileName: resumeFile?.name || (existingResumeIdForStart ? `existing:${existingResumeIdForStart}` : 'none')
    });

    // Job-share attribution (P1): capture share code from URL param or cookie.
    // First-touch — once attached to an application, this is locked server-side
    // and never overwritten on subsequent applies (UNIQUE candidate_id+job_id
    // would block a second apply anyway).
    const sourceShareCode = captureShareCode();
    if (sourceShareCode) {
      log('APPLICATION_SERVICE', 'Share-link attribution detected', { sourceShareCode });
    }

    let startSucceeded = false;
    try {
      const result = await applyApi.startEnhanced(
        jobId,
        {
          ...(tenantId ? { tenantId } : {}),
          email: user?.email || email,
          fullName: fullName || user?.fullName || 'Candidate',
          // Send E.164 (dial code + national) so the backend stores an
          // unambiguous number on both candidates.phone and the application.
          phone: toE164(dialCode, phone),
          // ✅ Reuse existing resume when candidate kept their on-file copy (May 4, 2026)
          existingResumeId: existingResumeIdForStart,
          source: isTenantDomain ? 'careersite' : 'vhire',
          // P1: recruiter referral attribution
          ...(sourceShareCode ? { sourceShareCode } : {}),
        },
        fileToSend
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (result.data || result) as any;

      if (result.success && data.sessionId) {
        startSucceeded = true;
        log('DEBUG', `API response: sessionId=${data.sessionId}, matchScore=${data.matchScore}, questions=${data.questions?.length}`);

        logSuccess('APPLICATION_SERVICE', 'Apply started', {
          sessionId: data.sessionId, matchScore: data.matchScore, questionsCount: data.questions?.length
        });

        setSessionId(data.sessionId);

        const resolvedMatchScore = data.matchScore !== undefined ? Math.min(100, data.matchScore ?? 0) : null;
        if (resolvedMatchScore !== null) {
          setMatchScore(resolvedMatchScore);
        }
        // Dev-only trace for skills-analysis debugging (gated; no raw dump).
        log('MATCH', 'matchAnalysis', {
          matchScore: data.matchScore,
          matchedSkills: data.matchAnalysis?.matchedSkills?.length || 0,
          missingSkills: data.matchAnalysis?.missingSkills?.length || 0,
          hasMatchAnalysis: !!data.matchAnalysis,
        });
        setMatchAnalysis(data.matchAnalysis || null);

        if (data.parsedData) {
          logSuccess('RESUME_SERVICE', 'Resume parsed', { skills: data.parsedData.skills?.length });
          setParsedData(data.parsedData);
          if (data.parsedData.fullName && !fullName) setFullName(data.parsedData.fullName);
          // parsedData.mobile mirrors the parser's column name; split it into
          // dial code + national so a resume-parsed international number keeps
          // its country instead of being mislabeled +91.
          if (data.parsedData.mobile && !phone) {
            const parsed = parsePhone(data.parsedData.mobile);
            setPhone(parsed.national);
            setDialCode(parsed.dialCode);
          }
        }

        if (data.arenaSessionId) {
          setArenaSessionId(data.arenaSessionId);
          log('ARENA', `Session ID: ${data.arenaSessionId}`);
        }

        if (data.questions?.length > 0) {
          const apiPlatformQuestions = data.questions
            .filter((q: { source?: string }) => q.source === 'platform')
            .map((q: Question) => ({
              id: q.id,
              text: q.text,
              type: q.type,
              source: q.source,
              required: q.required ?? true,
              options: q.options,
              conditionalFields: q.conditionalFields,
              conditionalField: q.conditionalField,
              category: q.category,
              validation: q.validation,
            }));
          if (apiPlatformQuestions.length > 0) setPlatformQuestions(apiPlatformQuestions);

          const apiTenantQuestions = data.questions
            .filter((q: { source?: string }) => q.source === 'tenant')
            .map((q: Question) => ({
              id: q.id,
              text: q.text,
              type: q.type,
              source: q.source,
              required: q.required ?? true,
              options: q.options,
              conditionalFields: q.conditionalFields,
              conditionalField: q.conditionalField
            }));
          setTenantQuestions(apiTenantQuestions);

          const apiJobQuestions = data.questions
            .filter((q: { source?: string }) => q.source === 'job')
            .map((q: Question) => ({
              id: q.id,
              text: q.text,
              type: q.type,
              source: q.source,
              required: q.required ?? true,
              options: q.options,
              conditionalFields: q.conditionalFields,
              conditionalField: q.conditionalField
            }));
          setJobQuestions(apiJobQuestions);

          const apiAiQuestions = data.questions
            .filter((q: { source?: string }) => q.source === 'ai')
            .map((q: Question) => ({
              id: q.id,
              text: q.text,
              type: q.type,
              source: q.source,
              required: q.required ?? true,
              options: q.options,
              difficulty: q.difficulty,
              skillAssessed: q.skillAssessed,
              timeLimit: q.timeLimit || 10
            }));
          setAiQuestions(apiAiQuestions);

          log('QUESTIONS', `Loaded - Tenant: ${apiTenantQuestions.length}, Job: ${apiJobQuestions.length}, AI: ${apiAiQuestions.length}`);
        }

        if (data.savedAnswers) {
          setAnswers(prev => ({ ...data.savedAnswers, ...prev }));
          log('PREFILL', `Pre-filled ${Object.keys(data.savedAnswers).length} saved answers`);
        }

        // Persist session to localStorage (Feb 24, 2026 - survive tab close)
        savePersistedSession(jobId, {
          sessionId: data.sessionId,
          step: 'questions',
          email: user?.email || email,
          fullName: fullName || user?.fullName || '',
          phone,
          dialCode,
          matchScore: resolvedMatchScore,
          matchAnalysis: data.matchAnalysis || null,
          parsedData: data.parsedData || null,
          arenaSessionId: data.arenaSessionId || null,
          tenantQuestions: data.questions?.filter((q: { source?: string }) => q.source === 'tenant') || [],
          jobQuestions: data.questions?.filter((q: { source?: string }) => q.source === 'job') || [],
          aiQuestions: data.questions?.filter((q: { source?: string }) => q.source === 'ai') || [],
          platformQuestions: data.questions?.filter((q: { source?: string }) => q.source === 'platform') || [],
          createdAt: Date.now()
        });
        log('SESSION_PERSIST', 'Saved session to localStorage');

        // Track returning candidate status (Feb 11, 2026 - Issue 1 fix)
        if (data.isReturningCandidate) {
          setIsReturningCandidate(true);
          log('RETURNING', 'Detected returning candidate');
        }
        // Already applied to THIS job (issue C, June 2026) — surface a notice
        // so the candidate can view their application instead of re-applying.
        if (data.alreadyApplied) {
          setAlreadyApplied(true);
          if (data.existingApplicationId) setExistingApplicationId(String(data.existingApplicationId));
          log('ALREADY_APPLIED', `Candidate already applied to this job (app ${data.existingApplicationId || 'unknown'})`);
        }
        if (data.hasExistingResume && data.existingResumeId) {
          setHasExistingResume(true);
          setExistingResumeId(data.existingResumeId);
          log('RESUME', `Using existing resume: ${data.existingResumeId}`);
        }
      } else {
        logError('APPLICATION_SERVICE', 'Apply failed', result.error);
        // ✅ Revert step so the candidate sees the error inline on the capture form
        // instead of being stuck on the questions screen with no sessionId (May 4, 2026)
        setStep('capture');
        setAutoSkippedCapture(false);
        setError(result.error || 'Failed to process application. Please try again.');
      }
    } catch (err) {
      logError('APPLICATION_SERVICE', 'Network error', err);
      setStep('capture');
      setAutoSkippedCapture(false);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsBackgroundProcessing(false);
      if (!startSucceeded) startingRef.current = false;
    }
  }, [validateCaptureForm, resumeFile, tenant?.id, jobId, email, user?.email, user?.fullName, fullName, phone, dialCode, isTenantDomain, canReuseExistingResume, reusableExistingResumeId]);

  // Auto-skip the capture step for fully recognized returning candidates (May 6, 2026)
  // Triggers when: authenticated user has email + name + phone + an existing resume on
  // file. We call handleStartApplication silently — it creates the application session
  // and lands the user on the questions step. Email + phone are platform identifiers and
  // should never be re-asked; the resume-replace affordance lives in a banner on the
  // landing step.
  useEffect(() => {
    if (
      !autoSkippedCapture &&
      !autoSkipAttemptedRef.current &&
      step === 'capture' &&
      !isLoading &&
      !isBackgroundProcessing &&
      !sessionId &&
      canReuseExistingResume &&
      !!email &&
      !!fullName?.trim() &&
      isValidNationalNumber(dialCode, phone)
    ) {
      log('AUTO_SKIP', 'Recognized candidate with existing resume — skipping capture step');
      autoSkipAttemptedRef.current = true;
      setAutoSkippedCapture(true);
      void handleStartApplication();
    }
  }, [
    autoSkippedCapture, step, isLoading, isBackgroundProcessing, sessionId,
    canReuseExistingResume, email, fullName, dialCode, phone, handleStartApplication
  ]);

  // Auto-skip the platform-questions step when answers are fresh (May 6, 2026)
  // Fires after the session is created. If the candidate's platform answers are
  // < 6 months old AND every required question for this job is already answered,
  // we advance past the questions step into job_questions / ai_questions / review.
  // Only applies on the auto-skip-capture path so a candidate who chose to revisit
  // the form still sees the questions screen.
  useEffect(() => {
    if (
      autoSkippedCapture &&
      platformAnswersFresh &&
      sessionId &&
      !isBackgroundProcessing &&
      step === 'questions'
    ) {
      // Guard (bug fix, May 16 2026): never auto-skip past required questions the
      // candidate hasn't answered. `platformAnswersFresh` only means they have
      // recent *prior* answers — THIS job's platform/tenant set may include
      // required questions not yet in `answers`. Skipping them strands the
      // candidate at review: submit fails validation and Back re-fires this skip,
      // an unwinnable loop. Use the same findMissingRequired check the Next and
      // Skip buttons already enforce, so auto-skip cannot diverge from them.
      if (findMissingRequired([...platformQuestions, ...tenantQuestions]).length > 0) {
        log('AUTO_SKIP', 'Required questions unanswered — staying on questions step');
        return;
      }
      log('AUTO_SKIP', 'Platform answers fresh — skipping questions step');
      if (jobQuestions.length > 0) {
        setStep('job_questions');
      } else if (aiQuestions.length > 0) {
        setCurrentAiQuestionIndex(0);
        setAiQuestionTimeLeft(aiQuestions[0]?.timeLimit || 10);
        setStep('ai_questions');
      } else {
        setStep('review');
      }
    }
    // findMissingRequired / answers / *Questions are read but kept out of deps:
    // re-running on every keystroke would yank the candidate to review mid-typing.
    // Evaluating the guard when step / sessionId / question lists settle is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSkippedCapture, platformAnswersFresh, sessionId, isBackgroundProcessing, step, jobQuestions, aiQuestions, platformQuestions, tenantQuestions]);

  // Re-enter the capture step so a recognized candidate can replace their resume.
  // Called from the resume-replace banner on the landing step.
  const enterCaptureForResumeReplace = useCallback(() => {
    setAutoSkippedCapture(false);
    // Release the start lock from the auto-skip apply so the candidate can re-run
    // handleStartApplication after replacing their resume (FE-3).
    startingRef.current = false;
    setStep('capture');
  }, []);

  // ✅ Required-answer validation helper (May 4, 2026)
  // Returns labels of unanswered required questions (and visible required conditionals).
  // Used as a frontend gate; backend completeApplication validates again as a backstop.
  const findMissingRequired = useCallback((questions: Question[]): string[] => {
    const missing: string[] = [];
    for (const q of questions) {
      if (q.required === false) continue;
      const a = answers[q.id];
      if (!a || a.trim() === '') {
        missing.push(q.text);
        continue;
      }
      // Validate visible conditional sub-fields whose parent answer matches showIf
      if (Array.isArray(q.conditionalFields)) {
        for (const cf of q.conditionalFields) {
          if (cf.showIf !== a) continue;
          const childId = cf.fieldId || `${q.id}_conditional`;
          const cv = answers[childId];
          if (!cv || cv.trim() === '') {
            missing.push(cf.fieldLabel || childId);
          }
        }
      }
    }
    return missing;
  }, [answers]);

  // Format a "missing fields" error message
  const missingErrorMessage = useCallback((missing: string[]): string => {
    if (missing.length === 1) return `Please answer: ${missing[0]}`;
    if (missing.length <= 3) return `Please answer: ${missing.join(', ')}`;
    return `Please answer ${missing.length} required questions, including: ${missing.slice(0, 2).join(', ')}`;
  }, []);

  // ✅ Submit-in-flight guard (May 4, 2026)
  // React state updates aren't synchronous; a fast double-click on the Submit
  // button can fire two completeEnhanced requests before isLoading propagates.
  // Backend dedupes via "existing application" check, but the second request
  // takes the UPDATE path with a different payload race — confusing logs.
  // A ref is the right primitive: read/write is synchronous, doesn't trigger
  // re-renders, and survives the React StrictMode double-mount.
  const submittingRef = useRef(false);

  // Submit application
  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) {
      log('APPLICATION_SERVICE', 'Submit ignored — already in flight');
      return;
    }
    if (!sessionId) {
      setError('Session expired. Please start over.');
      return;
    }

    // Mirror the capture-form phone into the screening answers map so a
    // candidate who only filled the `phone` capture input (returning user with
    // auto-skip, or any path that bypassed the platform_mobile question UI)
    // still satisfies the backend's required-question check.
    const needMobile = !!(phone && phone.trim() && !answers.platform_mobile);
    const mobileE164 = needMobile ? toE164(dialCode, phone) : null;
    // Build the submit payload locally instead of mutating React state in place (FE-6).
    const finalAnswers = mobileE164 ? { ...answers, platform_mobile: mobileE164 } : answers;
    // Keep state in sync for UI consistency, without depending on the async update.
    if (mobileE164) setAnswers(prev => ({ ...prev, platform_mobile: mobileE164 }));

    // ✅ Final backstop validation across all question sets (May 4, 2026)
    // Catches anything that slipped past per-step validation (e.g., conditional
    // fields whose parent answer changed after the step transition).
    const allMissing = [
      ...findMissingRequired([...platformQuestions, ...tenantQuestions]),
      ...findMissingRequired(jobQuestions),
      ...findMissingRequired(aiQuestions),
    ];
    if (allMissing.length > 0) {
      setError(missingErrorMessage(allMissing));
      return;
    }

    submittingRef.current = true;
    setIsLoading(true);
    setError(null);
    log('APPLICATION_SERVICE', 'Completing application', { sessionId });

    let succeeded = false;

    // Shared success housekeeping for both the direct path and the
    // timeout-recovery path (June 11, 2026).
    const finishSuccess = (applicationId?: string, matchReport?: unknown) => {
      // Clear saved answers and session from localStorage (Feb 11, 2026 + Feb 24, 2026)
      localStorage.removeItem(`apply_answers_${jobId}`);
      clearPersistedSession(jobId);
      log('CLEANUP', 'Cleared saved answers and session from localStorage');

      // Phase 3: Persist only the applicationId for the success page.
      // The real match report is fetched server-side via
      // GET /api/applications/:applicationId/match-report — we no longer
      // fabricate fields on the client.
      if (applicationId) {
        localStorage.setItem(`application_id_${jobId}`, String(applicationId));
        log('APPLICATION_SERVICE', 'Stored applicationId for success page', { applicationId });
      }
      // Inline report is included directly in the complete response so the
      // success page can render immediately without a second round trip.
      if (matchReport) {
        localStorage.setItem(`apply_match_report_${jobId}`, JSON.stringify(matchReport));
        log('APPLICATION_SERVICE', 'Stored real match report from complete response');
      }

      succeeded = true;
      // Job alert attribution (plan D9): if the candidate reached this job from
      // a digest link, record `applied` once. Fire-and-forget — it never
      // throws and nothing waits on it.
      recordJobAlertApplied(jobId);
      // Toast confirmation BEFORE redirect so the user sees feedback even if
      // the success-page navigation hangs (slow network, JS error). Without
      // this the submit was silently followed by router.push; if the redirect
      // failed the candidate had no signal their application went through.
      toast.success('Application submitted!');
      router.push(`/apply/${jobId}/success`);
    };

    // June 11, 2026 — false-failure recovery. A timeout/network error does NOT
    // mean the apply failed: in the 2026-06-10 incident the server finished
    // ~23s after the client gave up, leaving a committed application behind a
    // "Request timed out" error. Before declaring failure on an indeterminate
    // error, poll the candidate's own applications list for this job.
    const recoverSubmission = async (): Promise<boolean> => {
      log('APPLICATION_SERVICE', 'Indeterminate submit result — probing for committed application');
      toast.loading('Taking longer than usual — confirming your application…', { id: 'apply-recovery' });
      const hit = await probeForSubmittedApplication({
        jobIdentifiers: [jobId, job?.id],
        fetchApplications: async () => {
          const res = await candidateApi.getApplications();
          return {
            success: !!res.success,
            data: (res.data || []).map((a) => ({ id: a.id, jobId: a.jobId })),
          };
        },
      });
      toast.dismiss('apply-recovery');
      if (hit) {
        logSuccess('APPLICATION_SERVICE', 'Recovered: application was committed server-side', hit);
        finishSuccess(hit.applicationId);
        return true;
      }
      log('APPLICATION_SERVICE', 'Recovery probe found no application');
      return false;
    };

    try {
      const result = await applyApi.completeEnhanced(jobId, {
        sessionId,
        screeningAnswers: finalAnswers,
        additionalData: {},
        // ToS audit trail (migration 070) — the REVIEW-step checkbox state at submit
        acceptedTerms,
        termsVersion: TERMS_VERSION,
        answerMeta: answerTimingsRef.current,
      });

      if (result.success) {
        logSuccess('APPLICATION_SERVICE', 'Application submitted!');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const completeData = (result.data || result) as any;
        finishSuccess(
          completeData?.applicationId ? String(completeData.applicationId) : undefined,
          completeData?.matchReport,
        );
      } else if (isAlreadyAppliedError(result.error) || isIndeterminateSubmitError(result.error)) {
        // "Already applied" = a previous submit DID land (e.g. a retry after a
        // timeout) — recover it as a success rather than dead-ending the
        // candidate on an error they can't act on.
        const recovered = await recoverSubmission();
        if (!recovered) {
          logError('APPLICATION_SERVICE', 'Submit failed', result.error);
          setError(result.error || 'Failed to submit');
          toast.error(result.error || 'Couldn\'t submit. Please try again.');
        }
      } else {
        logError('APPLICATION_SERVICE', 'Submit failed', result.error);
        setError(result.error || 'Failed to submit');
        toast.error(result.error || 'Couldn\'t submit. Please try again.');
      }
    } catch (err) {
      logError('APPLICATION_SERVICE', 'Network error', err);
      const recovered = await recoverSubmission().catch(() => false);
      if (!recovered) {
        setError('Network error. Please try again.');
        toast.error('Network error. Please try again.');
      }
    } finally {
      setIsLoading(false);
      // Keep the lock if we're navigating away; release it so a real failure
      // can be retried with another click.
      if (!succeeded) submittingRef.current = false;
    }
  }, [sessionId, jobId, job?.id, answers, phone, dialCode, router, platformQuestions, tenantQuestions, jobQuestions, aiQuestions, acceptedTerms, findMissingRequired, missingErrorMessage]);

  // Send magic link
  const handleSendMagicLink = useCallback(async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);
    log('AUTH', 'Sending magic link', { email, tenant: tenant?.slug, jobId });

    const redirectUrl = `/apply/${jobId}`;
    const result = await login(email, tenant?.slug, tenant?.company_name || tenant?.name, jobId, redirectUrl);

    if (result.success) {
      setMagicLinkSent(true);
      logSuccess('AUTH', 'Magic link sent');
    } else {
      logError('AUTH', 'Failed to send magic link', result.error);
      setError(result.error || 'Failed to send login link');
    }
    setIsLoading(false);
  }, [email, tenant?.slug, tenant?.company_name, tenant?.name, jobId, login]);

  // Format mobile helper
  const formatMobile = useCallback((value: string) => {
    let cleaned = value.replace(/[^\d]/g, '');
    if (cleaned.startsWith('91') && cleaned.length > 10) cleaned = cleaned.substring(2);
    return cleaned.substring(0, 10);
  }, []);

  // Format answer value for display
  const formatAnswerValue = useCallback((value: string): string => {
    if (!value) return '';
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        if (parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0].name) {
          return parsed.map((loc: { name: string; city?: string }) =>
            loc.city ? `${loc.name}, ${loc.city}` : loc.name
          ).join(', ');
        }
        return parsed.join(', ');
      }
    } catch {
      // Not JSON
    }
    return value;
  }, []);

  // Calculate platform completion
  const answeredPlatform = platformQuestions.filter(q => answers[q.id]?.trim()).length;
  const platformPercentage = platformQuestions.length > 0
    ? Math.round((answeredPlatform / platformQuestions.length) * 100)
    : 0;

  // Progress calculations
  const hasJobQuestionsStep = jobQuestions.length > 0;
  const hasAiQuestionsStep = aiQuestions.length > 0;
  // ✅ May 6, 2026: When the capture step is auto-skipped (recognized candidate
  // with existing resume) and/or the platform-questions step is skipped (fresh
  // answers), drop them from the visible step count so the progress bar reflects
  // what the candidate actually sees.
  const skipCaptureFromCount = autoSkippedCapture && canReuseExistingResume;
  const skipQuestionsFromCount = skipCaptureFromCount && platformAnswersFresh;
  const totalSteps =
    (skipCaptureFromCount ? 0 : 1) +           // capture
    (skipQuestionsFromCount ? 0 : 1) +         // questions (platform + tenant)
    (hasJobQuestionsStep ? 1 : 0) +
    (hasAiQuestionsStep ? 1 : 0) +
    1;                                          // review

  const getCurrentStepNumber = useCallback(() => {
    if (step === 'auth') return 0;
    let n = 0;
    if (step === 'capture') return skipCaptureFromCount ? 0 : 1;
    n += skipCaptureFromCount ? 0 : 1;
    if (step === 'questions') return n + 1;
    n += skipQuestionsFromCount ? 0 : 1;
    if (step === 'job_questions') return n + 1;
    if (hasJobQuestionsStep) n += 1;
    if (step === 'ai_questions') return n + 1;
    if (hasAiQuestionsStep) n += 1;
    if (step === 'review') return n + 1;
    return 0;
  }, [step, hasJobQuestionsStep, hasAiQuestionsStep, skipCaptureFromCount, skipQuestionsFromCount]);

  const getOverallProgress = useCallback(() => {
    if (step === 'auth') return 0;

    let stepWeights: Record<string, number>;
    if (hasJobQuestionsStep && hasAiQuestionsStep) {
      stepWeights = { capture: 20, questions: 40, job_questions: 60, ai_questions: 80, review: 100 };
    } else if (hasJobQuestionsStep || hasAiQuestionsStep) {
      stepWeights = { capture: 25, questions: 50, job_questions: 75, ai_questions: 75, review: 100 };
    } else {
      stepWeights = { capture: 33, questions: 66, job_questions: 66, ai_questions: 66, review: 100 };
    }

    if (step === 'capture') {
      const fields = [email, fullName, phone, resumeFile || reusableExistingResumeId || null];
      const filled = fields.filter(f => f && (typeof f === 'string' ? f.trim() : true)).length;
      return Math.round((filled / fields.length) * stepWeights.capture);
    }

    if (step === 'questions') {
      const questionsOnScreen = [...platformQuestions, ...tenantQuestions];
      const answered = questionsOnScreen.filter(q => answers[q.id]?.trim()).length;
      const questionProgress = questionsOnScreen.length > 0 ? answered / questionsOnScreen.length : 1;
      return stepWeights.capture + Math.round(questionProgress * (stepWeights.questions - stepWeights.capture));
    }

    if (step === 'job_questions') {
      const answered = jobQuestions.filter(q => answers[q.id]?.trim()).length;
      const questionProgress = jobQuestions.length > 0 ? answered / jobQuestions.length : 1;
      return stepWeights.questions + Math.round(questionProgress * (stepWeights.job_questions - stepWeights.questions));
    }

    if (step === 'ai_questions') {
      const answered = aiQuestions.filter(q => answers[q.id]?.trim()).length;
      const questionProgress = aiQuestions.length > 0 ? answered / aiQuestions.length : 1;
      const prevWeight = hasJobQuestionsStep ? stepWeights.job_questions : stepWeights.questions;
      return prevWeight + Math.round(questionProgress * (stepWeights.ai_questions - prevWeight));
    }

    if (step === 'review') {
      return acceptedTerms ? 100 : 95;
    }

    return 0;
  }, [step, hasJobQuestionsStep, hasAiQuestionsStep, email, fullName, phone, resumeFile, reusableExistingResumeId, platformQuestions, tenantQuestions, answers, jobQuestions, aiQuestions, acceptedTerms]);

  const buildStepNames = useCallback(() => {
    const names: string[] = [];
    if (!skipCaptureFromCount) names.push('Your Info');
    if (!skipQuestionsFromCount) names.push('Profile');
    if (hasJobQuestionsStep) names.push('Job Questions');
    if (hasAiQuestionsStep) names.push('🤖 AI Quiz');
    names.push('Review');
    return names;
  }, [hasJobQuestionsStep, hasAiQuestionsStep, skipCaptureFromCount, skipQuestionsFromCount]);

  // Handle AI answer
  const handleAiAnswer = useCallback((answer: string) => {
    const currentQuestion = aiQuestions[currentAiQuestionIndex];
    answersRef.current = { ...answersRef.current, [currentQuestion.id]: answer };
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: answer }));
    // Record elapsed time for the application_answers projection (migration 070).
    const limit = currentQuestion.timeLimit || 10;
    answerTimingsRef.current[currentQuestion.id] = {
      timeTakenMs: Math.max(0, (limit - aiQuestionTimeLeft) * 1000),
    };
    // Stop this question's countdown now so an in-flight tick can't also advance
    // the index (double-skip) or complete the quiz (FE-2).
    if (aiTimerRef.current) {
      clearInterval(aiTimerRef.current);
      aiTimerRef.current = null;
    }

    setTimeout(() => {
      if (currentAiQuestionIndex < aiQuestions.length - 1) {
        setCurrentAiQuestionIndex(i => i + 1);
        setAiQuestionTimeLeft(aiQuestions[currentAiQuestionIndex + 1]?.timeLimit || 10);
      } else {
        // Answering the last question ends the quiz just like its timer
        // running out — both must complete the arena session.
        completeArenaSession();
        setStep('review');
      }
    }, 300);
  }, [aiQuestions, currentAiQuestionIndex, aiQuestionTimeLeft, completeArenaSession]);

  // Toggle expanded section
  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Questions step next handler
  const handleQuestionsNext = useCallback(() => {
    const missing = findMissingRequired([...platformQuestions, ...tenantQuestions]);
    if (missing.length > 0) {
      setError(missingErrorMessage(missing));
      return;
    }
    setError(null);
    if (jobQuestions.length > 0) {
      setStep('job_questions');
    } else if (aiQuestions.length > 0) {
      setCurrentAiQuestionIndex(0);
      setAiQuestionTimeLeft(aiQuestions[0]?.timeLimit || 10);
      setStep('ai_questions');
    } else {
      setStep('review');
    }
  }, [jobQuestions.length, aiQuestions, platformQuestions, tenantQuestions, findMissingRequired, missingErrorMessage]);

  // Skip platform questions handler for returning candidates with fresh answers (Feb 11, 2026)
  const handleSkipPlatformQuestions = useCallback(() => {
    // Even on "skip", required answers must exist — they came from saved profile prefill.
    // If anything is missing, force the candidate to fill it instead of silently skipping.
    const missing = findMissingRequired([...platformQuestions, ...tenantQuestions]);
    if (missing.length > 0) {
      setError(missingErrorMessage(missing));
      return;
    }
    setError(null);
    log('SKIP', 'Skipping platform questions - answers are fresh');
    if (jobQuestions.length > 0) {
      setStep('job_questions');
    } else if (aiQuestions.length > 0) {
      setCurrentAiQuestionIndex(0);
      setAiQuestionTimeLeft(aiQuestions[0]?.timeLimit || 10);
      setStep('ai_questions');
    } else {
      setStep('review');
    }
  }, [jobQuestions.length, aiQuestions, platformQuestions, tenantQuestions, findMissingRequired, missingErrorMessage]);

  // Job questions next handler
  const handleJobQuestionsNext = useCallback(() => {
    const missing = findMissingRequired(jobQuestions);
    if (missing.length > 0) {
      setError(missingErrorMessage(missing));
      return;
    }
    setError(null);
    if (aiQuestions.length > 0) {
      setCurrentAiQuestionIndex(0);
      setAiQuestionTimeLeft(aiQuestions[0]?.timeLimit || 10);
      setStep('ai_questions');
    } else {
      setStep('review');
    }
  }, [aiQuestions, jobQuestions, findMissingRequired, missingErrorMessage]);

  return {
    // Context
    tenant,
    isTenantDomain,
    tenantLoading,
    authLoading,
    user,
    isAuthenticated,
    
    // Flow state
    step,
    setStep,
    magicLinkSent,
    setMagicLinkSent,
    isLoading,
    error,
    setError,
    isBackgroundProcessing,
    
    // Capture state
    email,
    setEmail,
    fullName,
    setFullName,
    phone,
    setPhone,
    dialCode,
    setDialCode,
    formatMobile,
    
    // Job state
    job,
    jobId,
    
    // Resume state
    resumeFile,
    setResumeFile,
    parsedData,
    
    // Returning candidate state (Feb 11, 2026 - Issue 1 fix)
    isReturningCandidate,
    hasExistingResume,
    existingResumeId,
    canReuseExistingResume,
    existingResumeCreatedAt,
    existingResumeFresh,
    platformAnswersFresh,
    // Exposed so the questions step can gate the "answers up to date / skip"
    // card on there being NO actually-missing required answer (June 2026 fix —
    // the banner used to claim completeness while a required field was empty,
    // and Skip & Continue then silently no-op'd on the hidden validation error).
    findMissingRequired,
    // Already-applied-to-this-job (issue C, June 2026)
    alreadyApplied,
    existingApplicationId,
    // Auto-skip capture state (May 6, 2026)
    autoSkippedCapture,
    enterCaptureForResumeReplace,
    
    // Session & Match
    sessionId,
    matchScore,
    matchAnalysis,
    
    // Questions
    platformQuestions,
    tenantQuestions,
    jobQuestions,
    aiQuestions,
    answers,
    setAnswers,
    
    // AI Questions
    currentAiQuestionIndex,
    aiQuestionTimeLeft,
    handleAiAnswer,
    
    // Terms
    acceptedTerms,
    setAcceptedTerms,
    
    // Collapsible sections
    expandedSections,
    toggleSection,
    
    // Progress
    answeredPlatform,
    platformPercentage,
    hasJobQuestionsStep,
    hasAiQuestionsStep,
    totalSteps,
    getCurrentStepNumber,
    getOverallProgress,
    buildStepNames,
    
    // Handlers
    handleSendMagicLink,
    handleStartApplication,
    handleSubmit,
    handleQuestionsNext,
    handleJobQuestionsNext,
    handleSkipPlatformQuestions,
    formatAnswerValue,
    
    // Badges
    badgesEarned,
  };
}
